/**
 * Location Scheme Manager.
 *
 * List, create, edit, and delete location naming schemes for the
 * organization. Accessible from the Yard Map page header. Each scheme
 * is a reusable template that can be applied to any number of sections.
 */

import { useState } from "react";
import { Layers, Plus, Trash2, Pencil, Grid3X3 } from "lucide-react";
import { toast } from "@/stores/toastStore";
import {
  useLocationSchemes,
  useCreateLocationScheme,
  useDeleteLocationScheme,
  useUpdateLocationScheme,
} from "@/features/yards/api";
import type { LocationScheme, SegmentDefinition } from "@/types/locations";
import LocationSchemeEditor from "@/features/yards/LocationSchemeEditor";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface LocationSchemeManagerProps {
  onClose?: () => void;
}

function LocationSchemeManager({ onClose }: LocationSchemeManagerProps) {
  const { data: schemes, isLoading } = useLocationSchemes();
  const createMutation = useCreateLocationScheme();
  const updateMutation = useUpdateLocationScheme();
  const deleteMutation = useDeleteLocationScheme();

  const [showEditor, setShowEditor] = useState(false);
  const [editingScheme, setEditingScheme] = useState<LocationScheme | null>(
    null,
  );

  const handleCreate = (data: {
    name: string;
    description?: string;
    segments: SegmentDefinition[];
    separator: string;
  }) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        setShowEditor(false);
        toast.success("Location scheme created");
      },
      onError: () => toast.error("Failed to create scheme"),
    });
  };

  const handleUpdate = (data: {
    name: string;
    description?: string;
    segments: SegmentDefinition[];
    separator: string;
  }) => {
    if (!editingScheme) return;
    updateMutation.mutate(
      { id: editingScheme.id, data },
      {
        onSuccess: () => {
          setEditingScheme(null);
          toast.success("Location scheme updated");
        },
        onError: () => toast.error("Failed to update scheme"),
      },
    );
  };

  const handleDelete = (scheme: LocationScheme) => {
    if (
      !confirm(
        `Delete "${scheme.name}"? Sections using this scheme will lose their locations.`,
      )
    )
      return;
    deleteMutation.mutate(scheme.id, {
      onSuccess: () => toast.success("Scheme deleted"),
      onError: () => toast.error("Failed to delete scheme"),
    });
  };

  const segmentSummary = (scheme: LocationScheme) => {
    return (scheme.segments as SegmentDefinition[])
      .map((seg) => {
        if (seg.type === "fixed")
          return `${seg.name}: ${(seg.values ?? []).join(", ")}`;
        if (seg.start != null && seg.end != null)
          return `${seg.name}: ${seg.start}–${seg.end}`;
        return `${seg.name} (${seg.type})`;
      })
      .join(` ${scheme.separator} `);
  };

  // Show editor for create or edit
  if (showEditor || editingScheme) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[var(--text-primary)]">
            {editingScheme ? "Edit Scheme" : "New Location Scheme"}
          </h3>
        </div>
        <LocationSchemeEditor
          initialName={editingScheme?.name}
          initialDescription={editingScheme?.description ?? ""}
          initialSegments={
            editingScheme?.segments as SegmentDefinition[] | undefined
          }
          initialSeparator={editingScheme?.separator}
          onSave={editingScheme ? handleUpdate : handleCreate}
          onCancel={() => {
            setShowEditor(false);
            setEditingScheme(null);
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[var(--text-primary)]">
            Storage Schemes
          </h3>
          <p className="text-sm text-[var(--text-secondary)]">
            Reusable templates for generating storage location labels. Apply the
            same scheme to multiple storage areas.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowEditor(true)}>
            <Plus className="h-4 w-4" /> New Scheme
          </Button>
          {onClose && (
            <Button size="sm" variant="secondary" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner className="py-8" />
      ) : schemes && schemes.length > 0 ? (
        <div className="space-y-3">
          {schemes.map((scheme) => (
            <Card key={scheme.id} className="hover-lift">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-200 text-brand-600 dark:bg-brand-900/50 dark:text-brand-300">
                  <Grid3X3 className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-semibold text-[var(--text-primary)]">
                      {scheme.name}
                    </h4>
                    <Badge
                      variant={
                        scheme.total_locations != null ? "info" : "default"
                      }
                    >
                      {scheme.total_locations != null
                        ? `${scheme.total_locations.toLocaleString()} locations`
                        : "Template"}
                    </Badge>
                    <Badge variant="default">
                      {scheme.segments.length} segment
                      {scheme.segments.length !== 1 ? "s" : ""}
                    </Badge>
                  </div>
                  {scheme.description && (
                    <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
                      {scheme.description}
                    </p>
                  )}
                  <p className="mt-1 text-[10px] font-mono text-[var(--text-tertiary)]">
                    {segmentSummary(scheme)}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setEditingScheme(scheme)}
                    className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(scheme)}
                    className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-8 text-center max-w-md mx-auto">
            <Layers className="h-10 w-10 text-[var(--text-tertiary)]" />
            <h4 className="mt-3 text-sm font-medium text-[var(--text-primary)]">
              No storage schemes yet
            </h4>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              Location schemes define how spots in your storage areas are
              labeled (e.g., parking spaces, rack positions, bin numbers). You
              can create reusable templates here, or set up locations directly
              when adding sections on the Facility Map page.
            </p>
            <Button
              size="sm"
              className="mt-4"
              onClick={() => setShowEditor(true)}
            >
              <Plus className="h-4 w-4" /> Create a Scheme
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

export default LocationSchemeManager;
